import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useUIStore } from '@/lib/core/stores/useUIStore';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { useClientTranslation } from '@/lib/core/i18n';
import { LinkRouteUrl } from '@/lib/core/utils';
import { Toast } from '@/components/ui/toast';
import { SpinLoading } from '@/components/shared/loadings/spin';
import { getReservation } from '@/services/platform/data/booking';
import {
  getMewsVenues,
  getMewsBookingEngineConfiguration,
  useMewsCategories,
  useMewsMultiCategoryAllocation,
  buildMewsReservationGroupRequest,
  useMewsCreateReservationGroup,
  mapMewsCategoryDtoToItem,
} from '@/services/platform/data/booking/mews';
import type { MewsAgeConfigDto } from '@/services/platform/data/booking/mews';
import { getMembership } from '@/services/platform/data/profile';
import {
  BookFlowSunbedContentResponseModel,
  SunbedBookFlowWhereWhenHowManyWhichPropertiesModel,
  SunbedBookFlowWhereWhenHowManyWhichElementContentModelContentType,
  SunbedBookFlowFormItemPropertiesModel,
  SunbedBookFlowWhichPropertiesModel,
  SunbedBookFlowSunbedsQuantityConfirmationPropertiesModel,
  SunbedBookFlowSunbedsQuantityConfirmationElementContentModelContentType,
  SunbedBookFlowMoreDetailsPropertiesModel,
  SunbedBookFlowMoreDetailsElementContentModelContentType,
  MembershipDto,
  ReservationDetailDto,
  VenueDto,
} from '@/services/platform/api/model';
import { AppContext } from '@/lib/core/types';
import type {
  MewsCategory,
  MewsAllocation,
  MewsBookingState,
  MewsCategoryItem,
} from '@/services/platform/data/booking/mews/types';
import {
  MewsPaymentStep,
  forgetCheckout,
  recallCheckout,
  rememberCheckout,
  reviveMewsBooking,
  type MewsCheckoutState,
  type MewsPaidState,
} from '@/components/shared/checkout';
import { PaymentStepContentTypes, findStepProps, hasStep } from '../shared/steps';
import { SunbedBook } from '@/components/shared/booking/engine/flows/sunbed/book';
import { buildInitialAllocations } from '../shared/mews/allocations';
import { MewsWhichPage } from '../shared/mews/sheets/which';
import { MewsRateAndDistribution } from '../shared/mews/rate';
import { MewsMoreDetails } from '../shared/mews/details';
import { MewsConfirmation } from '../shared/mews/confirmation';

const emptyBooking: MewsBookingState = {
  venue: null,
  dateFrom: null,
  dateTo: null,
  adults: 0,
  children: 0,
  category: null,
  allocations: [],
  notes: '',
  paymentMethod: null,
  extras: [],
  voucherCode: '',
};

// ── BookFlowSunbed ─────────────────────────────────────────────────────────────

interface BookFlowSunbedProps {
  content: BookFlowSunbedContentResponseModel;
  context?: AppContext;
}

export function BookFlowSunbed({ content, context }: BookFlowSunbedProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useClientTranslation();
  const { locale } = useIntl();
  const setHideNavbar = useUIStore(state => state.setHideNavbar);
  const user = useAuthStore(state => state.user);
  const targetVenueNodeKey = (location.state as { venueNodeKey?: string | null } | null)?.venueNodeKey;

  const [membership, setMembership] = useState<MembershipDto | undefined>(undefined);
  useEffect(() => {
    getMembership().then(setMembership);
  }, []);
  const isMember = membership?.isActive === true;
  const isDisabled = (venue: VenueDto) =>
    ((venue.membersOnly ?? false) && !isMember) || (venue.isVenueClosed ?? false);

  // 0 = book (where/when/howmany)
  // 1 = which category (full page)
  // 2 = rate & distribution
  // 3 = more details
  // 4 = payment (only when the CMS authors a payment step; otherwise skipped for the /checkout route)
  // 5 = confirmation
  //
  // A local counter, not an index into `content.properties.steps` — those two were never the same
  // thing here (steps 1 and 5 have no block of their own), which is why the payment step can be
  // inserted without the CMS deciding its position.
  const [step, setStep] = useState<0 | 1 | 2 | 3 | 4 | 5>(0);
  const [reservation, setReservation] = useState<ReservationDetailDto | null>(null);
  /** What the payment step is charging for — set on the way into step 4, from either entry point. */
  const [pendingPayment, setPendingPayment] = useState<{ reservationGuid: string; amount: number } | null>(
    null
  );
  const [errorToastMessage, setErrorToastMessage] = useState<string | null>(null);
  const [errorToastKey, setErrorToastKey] = useState(0);

  const steps = content.properties?.steps?.items ?? [];

  // An authored payment step is what switches this flow from "navigate out to /checkout and come
  // back" to taking the payment inline. Absent, everything below falls through to the old path.
  const hasPaymentStep = hasStep(steps, PaymentStepContentTypes.Sunbed);

  /**
   * A payment left in flight by a redirect-based method (MB WAY sends the customer away and back
   * via `return_url`, which is this flow's own URL once payment is a step here — so the whole app
   * cold-reloads). `session.ts` already persisted everything needed to rebuild the payment step.
   *
   * Scoped to this flow's own path so a session left by a different flow is never picked up.
   */
  const recalledCheckout = useMemo(() => {
    // `mewsPaid` means the /checkout route already handled this payment and is handing the booking
    // back — its own effect below owns that, and both running would double-resume.
    if (!hasPaymentStep || (location.state as { mewsPaid?: MewsPaidState } | null)?.mewsPaid) {
      return undefined;
    }
    const recalled = recallCheckout('mews');
    return recalled?.returnPath === location.pathname ? recalled : undefined;
  }, [hasPaymentStep, location.state, location.pathname]);

  // Resuming, step 0 (Where/When/HowMany) would otherwise flash for the length of the revive and
  // the `getReservation` fetch below — true only for the resume cases; every other entry shows the
  // book flow immediately as it always has. Mirrors `flows/event/index.tsx`'s `isPreselecting`.
  const [isResuming, setIsResuming] = useState(() => {
    const state = location.state as { mewsPaid?: MewsPaidState } | null;
    return !!state?.mewsPaid || !!recalledCheckout;
  });

  useEffect(() => {
    setHideNavbar(true);
    return () => setHideNavbar(false);
  }, [setHideNavbar]);
  const [booking, setBooking] = useState<MewsBookingState>(emptyBooking);

  // Paid via the /checkout route: the reservation already exists and is now `Confirmed` — rehydrate
  // the booking exactly as it was before the redirect and fetch the authoritative reservation,
  // rather than replaying the booking steps (which would create a second reservation).
  const hasResumed = useRef(false);
  useEffect(() => {
    const state = location.state as { mewsPaid?: MewsPaidState } | null;
    const paid = state?.mewsPaid;
    if (!paid || hasResumed.current) return;
    hasResumed.current = true;

    setBooking(reviveMewsBooking(paid.booking));
    getReservation(paid.reservationGuid, locale).then(data => {
      if (data) setReservation(data);
      setStep(5);
      setIsResuming(false);
    });
  }, [location.state, locale]);

  // Reloaded mid-payment on the inline path. Where the branch above trusts its router state to mean
  // "paid", this one re-reads the reservation and lets its status decide: still held → back onto the
  // payment step, where `CheckoutScreen`'s own `getActivePayments` recovery picks the stalled
  // payment up rather than starting a second one; already `Confirmed` (the webhook won the race) →
  // straight to the confirmation.
  useEffect(() => {
    if (!recalledCheckout || hasResumed.current) return;
    hasResumed.current = true;

    setBooking(reviveMewsBooking(recalledCheckout.booking));
    getReservation(recalledCheckout.reservationGuid, locale).then(data => {
      if (data) setReservation(data);

      // A read that failed is not evidence the payment landed — `getReservation` returns undefined
      // on a 401, a 404 or a dropped connection. Treating that as "confirmed" would forget the only
      // record of what was being paid for and then step onto a confirmation with no reservation to
      // render. Going back to the payment step keeps the breadcrumb and lets `CheckoutScreen`'s own
      // `getActivePayments` recovery settle what actually happened.
      if (!data || (data.status ?? '').toLowerCase() === 'awaitingpayment') {
        setPendingPayment({
          reservationGuid: recalledCheckout.reservationGuid,
          amount: recalledCheckout.amountMajor,
        });
        setStep(4);
      } else {
        // Nothing left to pay — and nothing left to recover from, so the breadcrumb goes too.
        forgetCheckout();
        setStep(5);
      }
      setIsResuming(false);
    });
  }, [recalledCheckout, locale]);

  // ── Venues ──────────────────────────────────────────────────────────────────
  const serviceCategoryNodeKey = content.properties?.serviceCategory?.[0]?.id ?? undefined;
  const [venues, setVenues] = useState<VenueDto[]>([]);

  useEffect(() => {
    // Skip while resuming a paid checkout — the venue is about to be restored wholesale from the
    // revived booking, and this would otherwise race it (see the resume effect above).
    if (isResuming) return;
    getMewsVenues(undefined, serviceCategoryNodeKey).then(venues => {
      if (!venues?.length) return;
      setVenues(venues);
      const enabled = venues.filter(v => !isDisabled(v));

      // Keep the selection in sync with the venue the user arrived from.
      if (targetVenueNodeKey) {
        setBooking(prev => {
          if (prev.venue?.venueNodeKey === targetVenueNodeKey) return prev;
          const preselected = enabled.find(v => v.venueNodeKey === targetVenueNodeKey);
          return preselected ? { ...prev, venue: preselected } : prev;
        });
        return;
      }

      const singleVenue = enabled.length === 1 ? enabled[0] : undefined;
      if (singleVenue?.venueNodeKey) {
        setBooking(prev => {
          if (prev.venue?.venueNodeKey === singleVenue.venueNodeKey) return prev;
          return { ...prev, venue: singleVenue };
        });
      }
    });
  }, [serviceCategoryNodeKey, targetVenueNodeKey, isMember, isResuming]);

  const venueName = booking.venue?.name ?? undefined;
  const guestsMax = booking.venue?.guestsMax ?? undefined;
  const enterpriseId = booking.venue?.externalId ?? undefined;
  const serviceId = booking.venue?.bookableServiceExternalId ?? undefined;

  // ── Age configuration ────────────────────────────────────────────────────────
  const [ageConfig, setAgeConfig] = useState<MewsAgeConfigDto | undefined>(undefined);
  useEffect(() => {
    if (!serviceId) return;
    getMewsBookingEngineConfiguration(serviceId, enterpriseId).then(setAgeConfig);
  }, [serviceId, enterpriseId]);

  const {
    fetch: fetchCategories,
    categories,
    isLoading: categoriesLoading,
  } = useMewsCategories({
    serviceId,
    enterpriseId,
    dateFrom: booking.dateFrom,
    dateTo: booking.dateTo,
    adults: booking.adults,
    children: booking.children,
    locale,
    ageConfig,
    mapDto: dto => mapMewsCategoryDtoToItem(dto, 'per sunbed/daily'),
  });

  const { allocate, isPending: allocationPending } = useMewsMultiCategoryAllocation({
    serviceId,
    enterpriseId,
    dateFrom: booking.dateFrom,
    dateTo: booking.dateTo,
    ageConfig,
  });

  const { createReservationGroup, isPending: bookingPending } = useMewsCreateReservationGroup(locale);

  const handleBookNow = (grandTotal: number) => {
    const request = buildMewsReservationGroupRequest(booking, { ageConfig, user });
    createReservationGroup(request, {
      onSuccess: data => {
        setReservation(data);

        // A comped/free booking is already `Confirmed` — nothing to charge, unchanged path.
        if ((data.status ?? '').toLowerCase() !== 'awaitingpayment') {
          setStep(5);
          return;
        }

        const mewsCheckout: MewsCheckoutState = {
          reservationGuid: data.publicGuid!,
          amountMajor: grandTotal,
          currencyCode: 'EUR',
          returnPath: location.pathname,
          booking,
        };

        if (hasPaymentStep) {
          // Persisted before the step renders, not after: the customer can be redirected away by
          // MB WAY at any point once it does, and this is the only copy that survives that reload.
          rememberCheckout({ kind: 'mews', ...mewsCheckout });
          setPendingPayment({ reservationGuid: mewsCheckout.reservationGuid, amount: grandTotal });
          setStep(4);
          return;
        }

        const checkoutPath = LinkRouteUrl(context?.settings?.properties?.checkout?.route);
        if (!checkoutPath) {
          // No authored checkout page and no authored payment step means there is nowhere to take
          // payment. The reservation is already held server-side, so show what exists rather than
          // stranding the customer.
          console.warn('BookFlowSunbed: awaiting-payment reservation but no checkout route configured.');
          setErrorToastKey(key => key + 1);
          setErrorToastMessage(t('App.Labels.BookingError'));
          setStep(5);
          return;
        }

        navigate(checkoutPath, { state: { mewsCheckout } });
      },
      onError: message => {
        console.warn('handleBookNow: reservation group creation failed');
        setErrorToastKey(key => key + 1);
        setErrorToastMessage(message ?? t('App.Labels.BookingError'));
      },
    });
  };

  // Re-fetch categories whenever date, guest count, serviceId, or ageConfig availability changes.
  const hasAgeConfig = !!ageConfig;
  useEffect(() => {
    if (!booking.dateFrom || !booking.dateTo || booking.adults <= 0 || !serviceId) return;
    fetchCategories();
  }, [booking.dateFrom, booking.dateTo, booking.adults, booking.children, serviceId, locale, hasAgeConfig]);

  // Looked up by content type, not by array position: authoring the payment step shifts every
  // block after it, and `steps[1]` would then quietly hand the wrong content to the wrong screen.
  const step0Props = findStepProps<SunbedBookFlowWhereWhenHowManyWhichPropertiesModel>(
    steps,
    SunbedBookFlowWhereWhenHowManyWhichElementContentModelContentType.sunbedBookFlowWhereWhenHowManyWhich
  );

  const step1Props = findStepProps<SunbedBookFlowSunbedsQuantityConfirmationPropertiesModel>(
    steps,
    SunbedBookFlowSunbedsQuantityConfirmationElementContentModelContentType.sunbedBookFlowSunbedsQuantityConfirmation
  );

  const step2Props = findStepProps<SunbedBookFlowMoreDetailsPropertiesModel>(
    steps,
    SunbedBookFlowMoreDetailsElementContentModelContentType.sunbedBookFlowMoreDetails
  );

  // Extract which-step CMS props from step0's bookForm
  const formItems = (step0Props?.bookForm?.items ?? []).map(
    item => item.content?.properties as SunbedBookFlowFormItemPropertiesModel | undefined
  );
  const whichFormItem = formItems.find(
    item => item?.stepPicker?.items?.[0]?.content?.contentType === 'sunbedBookFlowWhich'
  );
  const whichProps = whichFormItem?.stepPicker?.items?.[0]?.content?.properties as
    SunbedBookFlowWhichPropertiesModel | undefined;

  const handleBack = () => {
    if (step === 0) navigate(-1);
    else setStep(prev => (prev - 1) as 0 | 1 | 2 | 3 | 4 | 5);
  };

  /**
   * Payment landed. The reservation is `Confirmed` server-side by now, so the confirmation screen
   * renders from a fresh read rather than from the pre-payment copy already in state.
   *
   * `CheckoutScreen` has already called `forgetCheckout()` before this runs.
   */
  const handlePaid = () => {
    if (!pendingPayment) return;
    getReservation(pendingPayment.reservationGuid, locale).then(data => {
      if (data) setReservation(data);

      // The money has landed and the session is already forgotten, so there is nothing left to
      // resume — but with neither a fresh read nor an earlier one there is also nothing for the
      // confirmation to render. Say so instead of stepping onto a screen that paints nothing.
      if (!data && !reservation) {
        setErrorToastKey(key => key + 1);
        setErrorToastMessage(t('App.Labels.BookingError'));
        return;
      }

      setPendingPayment(null);
      setStep(5);
    });
  };

  const handleContinueFromBook = () => setStep(1);

  const handleContinueFromWhich = (category: MewsCategory) => {
    const allocations = buildInitialAllocations(category, booking.adults, booking.children);
    allocate(allocations, ({ allocations: merged }) => {
      setBooking(prev => ({ ...prev, category, allocations: merged }));
      setStep(2);
    });
  };

  const handleBackToWhich = () => {
    setBooking(prev => ({ ...prev, category: null, allocations: [] }));
    setStep(1);
  };

  const handleAddCategory = (
    newCategory: MewsCategoryItem,
    currentAllocations: MewsAllocation[],
    onSuccess: (updated: MewsAllocation[]) => void
  ) => {
    const tempAlloc: MewsAllocation = {
      categoryId: newCategory.id,
      categoryName: newCategory.name,
      pricePerDay: newCategory.pricePerDay,
      quantity: 1,
      guestDistribution: [{ unitIndex: 0, adults: 1, children: 0 }],
    };
    allocate([...currentAllocations, tempAlloc], ({ allocations: merged }) => {
      onSuccess(merged);
    });
  };

  return (
    <div className="pt-safe-area flex h-full flex-col bg-white">
      {isResuming && <SpinLoading wrapperClassName="min-h-screen" />}

      {!isResuming && step === 0 && (
        <SunbedBook
          cmsProps={step0Props}
          booking={booking}
          venueName={venueName}
          venues={venues}
          isMember={isMember}
          guestsMax={guestsMax}
          ageConfig={ageConfig}
          onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
          onBack={handleBack}
          onContinue={handleContinueFromBook}
          step={2}
        />
      )}

      {!isResuming && step === 1 && (
        <MewsWhichPage
          cmsProps={whichProps}
          showRateLabel={step0Props?.showRateLabel}
          categories={categoriesLoading ? [] : categories}
          selected={booking.category}
          onContinue={handleContinueFromWhich}
          onBack={handleBack}
          isLoading={allocationPending}
          step={2}
        />
      )}

      {!isResuming && step === 2 && (
        <MewsRateAndDistribution
          cmsProps={step1Props}
          booking={booking}
          categories={categories}
          ageConfig={ageConfig}
          guestsMax={guestsMax}
          onAddCategory={handleAddCategory}
          isAddingCategory={allocationPending}
          onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
          onBack={handleBack}
          onContinue={() => setStep(3)}
          onBackToWhich={handleBackToWhich}
          step={2}
        />
      )}

      {!isResuming && step === 3 && (
        <MewsMoreDetails
          cmsProps={step2Props}
          booking={booking}
          onBookingChange={updates => setBooking(prev => ({ ...prev, ...updates }))}
          onBack={handleBack}
          onContinue={handleBookNow}
          isSubmitting={bookingPending}
        />
      )}

      {!isResuming && step === 4 && pendingPayment && (
        <MewsPaymentStep
          reservationGuid={pendingPayment.reservationGuid}
          amountMajor={pendingPayment.amount}
          currencyCode="EUR"
          booking={booking}
          context={context}
          onBack={handleBack}
          onSuccess={handlePaid}
          embedded
        />
      )}

      {!isResuming && step === 5 && reservation && (
        <MewsConfirmation
          booking={booking}
          reservation={reservation}
          onGoHome={() => navigate('/')}
          onSeeBookingDetails={() =>
            navigate(
              `${context?.settings?.properties?.bookingDetail?.route?.path}/${reservation?.publicGuid}`
            )
          }
        />
      )}

      <Toast
        key={errorToastKey}
        variant="error"
        open={errorToastMessage !== null}
        message={errorToastMessage ?? ''}
        onDismiss={() => setErrorToastMessage(null)}
      />
    </div>
  );
}
